import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Search, Check, X, RefreshCw, AlertCircle } from 'lucide-react';
import { vendorService } from '../services/api';
import { useToast } from '../hooks/useToast';
import DateRangePicker from '../components/DateRangePicker';

const getInquiryStatusBadgeClass = (status) => {
  const s = (status || 'PENDING').toUpperCase();
  switch(s) {
    case 'NEW':
    case 'PENDING':
      return 'bg-blue-50 text-blue-700 border-blue-100';
    case 'HOT':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    case 'APPROVED':
    case 'WON':
      return 'bg-green-50 text-green-800 border-green-200';
    case 'WARM':
      return 'bg-orange-50 text-orange-700 border-orange-100';
    case 'COLD':
      return 'bg-slate-50 text-slate-650 border-slate-200';
    case 'REJECTED':
    case 'LOST':
      return 'bg-rose-50 text-rose-700 border-rose-100';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-150';
  }
};

const getInquirySelectColorClass = (status) => {
  const s = (status || 'PENDING').toUpperCase();
  switch(s) {
    case 'NEW':
    case 'PENDING':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'HOT':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'WARM':
      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'COLD':
      return 'bg-slate-50 text-slate-650 border-slate-200';
    case 'REJECTED':
    case 'LOST':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'APPROVED':
    case 'WON':
      return 'bg-green-50 text-green-800 border-green-200';
    default:
      return 'bg-white text-gray-700 border-gray-200';
  }
};

const formatInquiryStatusLabel = (status) => {
  const s = (status || 'NEW').toUpperCase();
  switch(s) {
    case 'PENDING':
    case 'NEW':
      return 'New';
    case 'HOT':
      return 'Hot';
    case 'WARM':
      return 'Warm';
    case 'COLD':
      return 'Cold';
    case 'REJECTED':
    case 'LOST':
      return 'Lost';
    case 'APPROVED':
    case 'WON':
      return 'Won';
    default:
      return s;
  }
};


const Inquiries = () => {
  const { toast } = useToast();
  const [inquiries, setInquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '', 'PENDING', 'APPROVED', 'REJECTED'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(true); // main wrapper collapse

  const fetchInquiries = async (overrideFilters = null) => {
    const startVal = overrideFilters ? overrideFilters.startDate : startDate;
    const endVal = overrideFilters ? overrideFilters.endDate : endDate;
    if ((startVal && !endVal) || (!startVal && endVal)) {
      return;
    }

    setLoading(true);
    try {
      const filters = {};
      const statusVal = overrideFilters ? overrideFilters.status : statusFilter;
      const searchVal = overrideFilters ? overrideFilters.keyword : searchText;
      const pageVal = overrideFilters ? overrideFilters.page : currentPage;
      const sizeVal = overrideFilters ? overrideFilters.pageSize : pageSize;

      if (statusVal) filters.status = statusVal;
      if (searchVal) filters.keyword = searchVal;
      if (startVal) filters.startDate = startVal;
      if (endVal) filters.endDate = endVal;

      const res = await vendorService.getMyInquiries(filters, pageVal, sizeVal);
      if (res.success && res.data) {
        setInquiries(res.data.inquiries || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error("Error loading inquiries:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, [currentPage, pageSize, statusFilter, startDate, endDate]);

  // Handle search submit/trigger
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setCurrentPage(0);
      fetchInquiries();
    }
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleSearchSubmit = () => {
    setCurrentPage(0);
    fetchInquiries();
  };

  const handleResetFilters = () => {
    setSearchText('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    setPageSize(10);
    setCurrentPage(0);
    fetchInquiries({
      status: '',
      keyword: '',
      startDate: '',
      endDate: '',
      page: 0,
      pageSize: 10
    });
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      const res = await vendorService.updateInquiryStatus(inquiryId, newStatus);
      if (res.success) {
        // Update local list
        setInquiries(prev =>
          prev.map(inq => inq.id === inquiryId ? { ...inq, status: newStatus } : inq)
        );
        toast.success(`Booking status updated to ${newStatus.toLowerCase()} successfully.`);
      } else {
        toast.error("Failed to update status: " + res.error);
      }
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error("An error occurred while updating status.");
    }
  };


  const toggleAccordion = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(0);
      
      let start = Math.max(1, currentPage - 1);
      let end = Math.min(totalPages - 2, currentPage + 1);
      
      if (currentPage <= 1) {
        end = 3;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 4;
      }
      
      if (start > 1) {
        pages.push('ellipsis1');
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < totalPages - 2) {
        pages.push('ellipsis2');
      }
      
      pages.push(totalPages - 1);
    }
    return pages;
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="border-b border-gray-100 pb-5 mb-6 space-y-3">
        <h2 className="text-xl font-bold text-gray-800">Booking</h2>
        <div className="flex items-center gap-3 bg-sky-50/40 border border-sky-100 rounded-xl p-4 text-xs md:text-sm text-sky-900">
          <AlertCircle className="text-[#2ab391] flex-shrink-0" size={18} />
          <span className="font-semibold font-sans">
            To receive inquiries from architects and designers, list your business.
          </span>
        </div>
      </div>

      {/* Control row (Display, Status, Dates and Search) */}
      <div className="bg-white border border-gray-200/80 rounded-2xl p-5 shadow-xs mb-6 space-y-4">
        {/* Top filter row: Search and Status */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center">
          {/* Left: Status Quick Filter */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Status</span>
            <div className="relative min-w-[120px] flex-1 md:flex-initial">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(0);
                }}
                className="w-full bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-semibold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer"
              >
                <option value="">All</option>
                <option value="NEW">New</option>
                <option value="HOT">Hot</option>
                <option value="WARM">Warm</option>
                <option value="COLD">Cold</option>
                <option value="LOST">Lost</option>
                <option value="WON">Won</option>
              </select>
              <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                <ChevronDown size={12} />
              </span>
            </div>
          </div>

          {/* Right: Search box */}
          <div className="relative w-full md:w-72">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
              <Search size={14} />
            </span>
            <input
              type="text"
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyPress}
              placeholder="Search inquiries..."
              className="w-full bg-white border border-gray-200 rounded-xl pl-9 pr-9 py-1.5 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] transition-all"
            />
            {searchText && (
              <button
                onClick={() => {
                  setSearchText('');
                  setCurrentPage(0);
                  fetchInquiries({
                    status: statusFilter,
                    keyword: '',
                    startDate: startDate,
                    endDate: endDate,
                    page: 0,
                    pageSize: pageSize
                  });
                }}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Bottom filter row: Date Range and Reset Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-gray-100">
          <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-row sm:items-center sm:gap-3">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Date Range:</span>
            
            {/* Responsive DateRangePicker */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:inline">Date:</span>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                  setCurrentPage(0);
                }}
                placeholder="Select Date Range"
                className="w-full sm:w-56"
              />
            </div>
          </div>

          {/* Reset Filters button */}
          {(searchText || statusFilter || startDate || endDate || pageSize !== 10) && (
            <button
              onClick={handleResetFilters}
              className="flex items-center justify-center gap-1.5 text-xs font-bold text-rose-500 hover:text-rose-600 bg-rose-50 hover:bg-rose-100/70 border border-rose-100 rounded-xl px-3 py-1.5 cursor-pointer transition-colors w-full sm:w-auto"
            >
              <RefreshCw size={13} />
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* Main Accordion Panel (Booking Details) */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-xs bg-white mb-6">
        {/* Accordion header */}
        <div
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
          className="bg-gray-50/30 px-5 py-4 flex justify-between items-center border-b border-gray-200 cursor-pointer hover:bg-gray-50/80 transition-colors select-none"
        >
          <span className="font-bold text-gray-700 text-sm tracking-wide">
            Booking Details
          </span>
          <span className="text-[#2ab391]">
            {isAccordionOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </div>

        {/* Accordion Body contents */}
        {isAccordionOpen && (
          <div className="divide-y divide-gray-100">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <RefreshCw size={24} className="animate-spin text-[#2ab391]" />
              </div>
            ) : inquiries.length === 0 ? (
              <div className="py-16 text-center text-gray-400 font-semibold text-sm">
                No bookings found matching filters.
              </div>
            ) : (
              inquiries.map(inq => {
                const isExpanded = expandedId === inq.id;

                // Status badge styling
                const badgeClass = getInquiryStatusBadgeClass(inq.status);

                return (
                  <div key={inq.id} className="w-full">
                    {/* Collapsed Inquiry Summary Row */}
                    <div
                      onClick={() => toggleAccordion(inq.id)}
                      className="px-5 py-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-gray-50/30 transition-colors select-none"
                    >
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 items-center">
                        {/* Name and Phone */}
                        <div>
                          <div className="font-bold text-gray-800 text-sm">{inq.name}</div>
                          <div className="text-xs text-gray-500 font-medium mt-0.5">{inq.phone}</div>
                        </div>

                        {/* Date */}
                        <div className="text-xs text-gray-500 sm:text-right font-medium">
                          {formatDate(inq.created_at)}
                        </div>
                      </div>

                      {/* Status and Chevron Caret */}
                      <div className="flex items-center gap-3">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${badgeClass}`}>
                          {formatInquiryStatusLabel(inq.status)}
                        </span>
                        <span className="text-gray-450">
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </span>
                      </div>
                    </div>

                    {/* Expanded Accordion details */}
                    {isExpanded && (
                      <div className="bg-gray-50/20 px-5 pb-5 pt-2 border-t border-gray-100 space-y-4">
                        <div className="text-sm mt-2">
                          <div>
                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                              Inquiry Message
                            </span>
                            <span className="text-gray-700 text-xs font-medium leading-relaxed bg-white border border-gray-200/80 p-3 rounded-xl block shadow-xs whitespace-pre-line">
                              {inq.message}
                            </span>
                          </div>
                        </div>

                        {/* Status Change action dropdown */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 border-t border-gray-150 pt-4">
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Update Status:</span>
                          <div className="relative w-full sm:w-auto">
                            <select
                              value={inq.status === 'PENDING' ? 'NEW' : inq.status === 'APPROVED' ? 'WON' : inq.status === 'REJECTED' ? 'LOST' : inq.status}
                              onChange={(e) => handleStatusChange(inq.id, e.target.value)}
                              className={`w-full sm:w-auto border rounded-xl px-3 py-2 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-[#2ab391] cursor-pointer transition-colors ${getInquirySelectColorClass(inq.status)}`}
                            >
                              <option value="NEW" className="bg-blue-50 text-blue-700 font-bold">New</option>
                              <option value="HOT" className="bg-emerald-50 text-emerald-700 font-bold">Hot</option>
                              <option value="WARM" className="bg-amber-50 text-amber-700 font-bold">Warm</option>
                              <option value="COLD" className="bg-slate-50 text-slate-650 font-bold">Cold</option>
                              <option value="LOST" className="bg-rose-50 text-rose-700 font-bold">Lost</option>
                              <option value="WON" className="bg-green-50 text-green-850 font-bold">Won</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Footer controls: pagination entries text, display limit, and links */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-500 font-medium border-t border-gray-100 pt-5 mt-6">
        {/* Left: Entries Info */}
        <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
          {total === 0 ? (
            'No records available'
          ) : (
            `Showing ${currentPage * pageSize + 1} to ${Math.min((currentPage + 1) * pageSize, total)} of ${total} entries`
          )}
        </div>

        {/* Right: Rows per page and Page buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
          {/* Rows per page Selector */}
          <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Show</span>
            <div className="relative">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(0);
                }}
                className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                <ChevronDown size={12} />
              </span>
            </div>
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Entries</span>
          </div>

          {/* Pagination Buttons */}
          {totalPages > 0 && (
            <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
              >
                Previous
              </button>

              {/* Page numbers - Hidden on mobile, visible on sm and larger */}
              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers().map((pageNum, idx) => {
                  if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                    return (
                      <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                        ...
                      </span>
                    );
                  }
                  const isActive = currentPage === pageNum;
                  return (
                    <button
                      key={`page-${pageNum}`}
                      type="button"
                      onClick={() => setCurrentPage(pageNum)}
                      className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                        isActive
                          ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              {/* Page indicator text - Visible only on mobile */}
              <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-xs min-w-[70px] text-center">
                {currentPage + 1} / {totalPages}
              </span>

              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                disabled={currentPage >= totalPages - 1}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inquiries;
